import { z } from 'zod'
import { config } from '../config'

/**
 * The mocked auth module for prototype mode.
 *
 * Every authentication path on this site — password sign-in, Google, Microsoft,
 * signup, domain check, forgot password, reset password, resend verification —
 * ends here instead of at Keycloak when `config.prototypeMode` is on. Any email
 * and any password are accepted; the fixture decides only *where you land*, not
 * *whether* you get in.
 *
 * WHY A MODULE RATHER THAN AN `if` PER PAGE. Five screens start a flow, and every
 * one of them would otherwise need its own bypass branch. One module means one
 * place to read to know exactly what the prototype does and does not fake — and
 * one place to delete when the real IdP is wired up.
 *
 * SEC-FR-001 IS UNTOUCHED. No token is minted, no cookie is set, nothing is
 * written to web storage. The marketing app never held a session to begin with;
 * the console owns it. All this module does is skip a redirect to an identity
 * provider that is not running and send the browser to the console, which admits
 * the visitor under its own prototype mode.
 *
 * BUNDLE DISCIPLINE. The fixture is behind `await import()`, not a static import.
 * `routes.tsx` imports the auth pages eagerly (they are prerendered), so a static
 * JSON import here would land in the entry chunk and spend part of the marketing
 * app's 6.5KB of remaining first-paint headroom on data no visitor to the home
 * page will ever need. Loaded on demand it sits in an async chunk that `pnpm size`
 * correctly does not count.
 *
 * LATENCY IS FIXED, NOT RANDOM. The delays come from the fixture so the screens
 * exercise their real pending states (button spinners, the "Checking your
 * domain…" live region) while staying reproducible for a demo.
 */

interface MockAccountFixture {
  email: string
  password: string
  full_name: string
  workspace_id: string
  workspace_name: string
  email_verified: boolean
  hint: string
}

interface AuthFixture {
  policy: {
    accept_any_email: boolean
    accept_any_password: boolean
    allow_free_email_domains: boolean
    latency_ms: {
      sign_in: number
      domain_check: number
      password_reset: number
      resend_verification: number
    }
  }
  accounts: MockAccountFixture[]
  fallback_account: {
    workspace_id: string
    workspace_name: string
    email_verified: boolean
    hint: string
  }
  domain_checks: {
    domain: string
    valid: boolean
    existing_workspace?: string
    reason?: string
  }[]
  reset_tokens: { token: string; status: 'VALID' | 'EXPIRED' }[]
  verification: { expires_in_hours: number }
}

let fixturePromise: Promise<AuthFixture> | null = null

/** Memoised so typing in the email field does not re-fetch the chunk per keystroke. */
function loadFixture(): Promise<AuthFixture> {
  fixturePromise ??= import('../../../../mock_data/auth-mock.json').then(
    (module) => module.default as unknown as AuthFixture,
  )
  return fixturePromise
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/** What the prototype knows about the person signing in. */
export interface MockIdentity {
  email: string
  fullName: string
  workspaceId: string
  workspaceName: string
  emailVerified: boolean
  hint: string
}

function domainOf(email: string) {
  return email.split('@')[1]?.toLowerCase().trim() ?? ''
}

/**
 * Title-cases the local part so an arbitrary address still produces a plausible
 * name: `ana.mehta@newco.io` → "Ana Mehta". Cosmetic, but a demo that greets you
 * as `ana.mehta` reads as broken rather than as mocked.
 */
function nameFromEmail(email: string) {
  const local = email.split('@')[0] ?? ''
  const words = local
    .split(/[._\-+\d]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  return words.length ? words.join(' ') : 'Prototype User'
}

async function resolveIdentity(email: string): Promise<MockIdentity> {
  const fixture = await loadFixture()
  const normalised = email.trim().toLowerCase()
  const match = fixture.accounts.find((account) => account.email.toLowerCase() === normalised)

  if (match) {
    return {
      email: match.email,
      fullName: match.full_name,
      workspaceId: match.workspace_id,
      workspaceName: match.workspace_name,
      emailVerified: match.email_verified,
      hint: match.hint,
    }
  }

  const fallback = fixture.fallback_account
  return {
    email: normalised,
    fullName: nameFromEmail(normalised),
    workspaceId: fallback.workspace_id,
    workspaceName: fallback.workspace_name,
    emailVerified: fallback.email_verified,
    hint: fallback.hint,
  }
}

/** The listed demo accounts, for the prototype notice on the sign-in screen. */
export async function listMockAccounts(): Promise<
  { email: string; password: string; fullName: string; hint: string }[]
> {
  const fixture = await loadFixture()
  return fixture.accounts.map((account) => ({
    email: account.email,
    password: account.password,
    fullName: account.full_name,
    hint: account.hint,
  }))
}

export interface MockSignInResult {
  identity: MockIdentity
  /** Where the browser is sent next. Absolute for the console, in-app for a marketing route. */
  redirectTo: string
}

/**
 * Signs in without authenticating anything.
 *
 * The one branch worth keeping is `email_verified`: the fixture's unverified
 * account lands on the confirm-email screen rather than the console, so M00-23 is
 * reachable in the prototype instead of being a page nothing links to. Everything
 * else — password, provider, register — is accepted as-is.
 */
export async function mockSignIn(options: {
  email?: string
  password?: string
  provider?: 'google' | 'microsoft'
  register?: boolean
}): Promise<MockSignInResult> {
  const fixture = await loadFixture()
  await delay(fixture.policy.latency_ms.sign_in)

  // A social button carries no address, so invent one from the provider. It has to
  // be *some* identity or the console lands on a workspace picked from nothing.
  const email = options.email?.trim() || `demo.user@${options.provider ?? 'prototype'}.example`
  const identity = await resolveIdentity(email)

  if (!fixture.policy.accept_any_password && options.password) {
    const account = fixture.accounts.find(
      (candidate) => candidate.email.toLowerCase() === email.toLowerCase(),
    )
    if (account && account.password !== options.password) {
      throw new Error('That email and password do not match.')
    }
  }

  // A brand-new signup has nothing to verify against, so it goes straight in.
  const needsVerification = !options.register && !identity.emailVerified

  return {
    identity,
    redirectTo: needsVerification
      ? `/auth/verify-email?email=${encodeURIComponent(identity.email)}`
      : `${config.consoleUrl}/w/${identity.workspaceId}`,
  }
}

export type MockDomainVerdict =
  | { valid: true; existingWorkspace?: string }
  | { valid: false; reason: string }

/**
 * M00-18's domain check, served from the fixture.
 *
 * Free-mail addresses are ACCEPTED here, which is the deliberate difference from
 * production. The real rule exists because InsightAI is sold to organisations; in a
 * prototype whose whole point is that any dummy input works, rejecting the address
 * most people will actually type is friction with nothing behind it. The fixture
 * keeps two genuine rejections (`blocked.example`, `mailinator.com`) so the invalid
 * branch is still demonstrable.
 */
export async function mockCheckDomain(email: string): Promise<MockDomainVerdict> {
  const fixture = await loadFixture()
  await delay(fixture.policy.latency_ms.domain_check)

  const entry = fixture.domain_checks.find((check) => check.domain === domainOf(email))
  if (!entry) return { valid: true }
  return entry.valid
    ? { valid: true, existingWorkspace: entry.existing_workspace }
    : { valid: false, reason: entry.reason ?? 'BLOCKED' }
}

/** Always resolves: the page's message is non-committal by design (account enumeration). */
export async function mockRequestPasswordReset(): Promise<void> {
  const fixture = await loadFixture()
  await delay(fixture.policy.latency_ms.password_reset)
}

/**
 * Any token works except the fixture's explicitly expired one, so `?token=anything`
 * reaches the form while `?token=expired-reset-token` still demonstrates the
 * expired-link path.
 */
export async function mockResetPassword(token: string | null): Promise<{ ok: boolean }> {
  const fixture = await loadFixture()
  await delay(fixture.policy.latency_ms.password_reset)
  const entry = fixture.reset_tokens.find((candidate) => candidate.token === token)
  return { ok: entry?.status !== 'EXPIRED' }
}

export async function mockResendVerification(): Promise<void> {
  const fixture = await loadFixture()
  await delay(fixture.policy.latency_ms.resend_verification)
}

/**
 * Step 1 of signup, with the work-email refinement dropped.
 *
 * `signupStepSchemas.email` in @insightai/auth rejects free-mail domains, and that
 * package is shared with the console — relaxing it there would change the console's
 * behaviour too. Selecting a different schema in prototype mode keeps the change
 * inside this app, which is the whole requirement.
 */
export const prototypeEmailStepSchema = z.object({
  email: z.string().trim().min(1, 'Enter your email address').email('Enter a valid email address'),
})
